import { gql } from '@apollo/client';
import { FormattedConnector } from 'src/hooks/useConnectorAccount';
import { AccountSelectionInput, ConnectorType } from 'src/utils/gql/types';

const conversationMessageFields = `
  id
  createdAt
  role
  text
  helpful
  table {
    dateRange {
      from
      to
    }
    rows {
      facebook {
        splitValues {
          key
          value
        }
        metricValues {
          key
          value
        }
      }
      googleAds {
        splitValues {
          key
          value
        }
        metricValues {
          key
          value
        }
      }
      shopify {
        splitValues {
          key
          value
        }
        metricValues {
          key
          value
        }
      }
      googleAnalytics4 {
        splitValues {
          key
          value
        }
        metricValues {
          key
          value
        }
      }
      customFormula {
        splitValues {
          key
          value
        }
        metricValues {
          key
          value
        }
      }
      database {
        splitValues {
          key
          value
        }
        metricValues {
          key
          value
        }
      }
    }
  }
`;

export const REGISTER = gql`
  mutation register($registerData: RegisterUserInput!) {
    register(registerData: $registerData)
  }
`;

export const RESET_PASSWORD = gql`
  mutation resetPassword($email: String!) {
    resetPassword(email: $email)
  }
`;

export const SET_NEW_PASSWORD = gql`
  mutation setNewPassword($secret: String!, $newPassword: String!) {
    setNewPassword(secret: $secret, newPassword: $newPassword)
  }
`;

export const CHANGE_PASSWORD = gql`
  mutation changePassword($input: ChangePasswordInput!) {
    changePassword(input: $input)
  }
`;

export const UPDATE_COMPANY = gql`
  mutation updateCompany($newCompanyData: CompanyInput!) {
    updateCompany(newCompanyData: $newCompanyData) {
      name
      industry
      companyDescription
      usageOptions
    }
  }
`;

export const CREATE_CUSTOM_FORMULA = gql`
  mutation createCustomFormula($customFormulaData: CustomFormulaInput!) {
    createCustomFormula(customFormulaData: $customFormulaData) {
      id
    }
  }
`;

export const UPDATE_CUSTOM_FORMULA = gql`
  mutation updateCustomFormula($customFormulaData: UpdateCustomFormulaInput!) {
    updateCustomFormula(customFormulaData: $customFormulaData) {
      name
      id
      formulaData {
        label
        value
        type
        connectorType
        metricId
        filters {
          key
          filterGroupOr {
            rulesAnd {
              operator
              values
            }
          }
        }
      }
      format {
        type
        description
        prefix
        suffix
        isDecimal
      }
    }
  }
`;

export const DELETE_CUSTOM_FORMULA = gql`
  mutation deleteCustomFormula($id: String!) {
    deleteCustomFormula(id: $id)
  }
`;

export const DELETE_CONNECTOR = gql`
  mutation deleteConnector($connectorId: String!) {
    deleteConnector(connectorId: $connectorId)
  }
`;

export const ADD_FACEBOOK_CONNECTOR = gql`
  mutation addFacebookConnector($code: String!) {
    addFacebookConnector(code: $code)
  }
`;

export const ADD_GOOGLE_ADS_CONNECTOR = gql`
  mutation addGoogleAdsConnector($code: String!) {
    addGoogleAdsConnector(code: $code)
  }
`;

export const ADD_GOOGLE_ANALYTICS_CONNECTOR = gql`
  mutation addGoogleAnalyticsConnector($code: String!) {
    addGoogleAnalyticsConnector(code: $code)
  }
`;

export const ADD_GOOGLE_SEARCH_CONSOLE_CONNECTOR = gql`
  mutation addGoogleSearchConsoleConnector($code: String!) {
    addGoogleSearchConsoleConnector(code: $code)
  }
`;

export const ADD_SHOPIFY_CONNECTOR = gql`
  mutation addShopifyConnector($code: String!) {
    addShopifyConnector(code: $code)
  }
`;

export const TEST_MAGENTO_CONNECTION = gql`
  mutation testMagentoConnection($input: MagentoConnectionInput!) {
    testMagentoConnection(input: $input) {
      ok
      authMode
      baseUrl
      error
      currency
      timezone
    }
  }
`;

export const ADD_MAGENTO_CONNECTOR = gql`
  mutation addMagentoConnector($input: MagentoConnectionInput!) {
    addMagentoConnector(input: $input)
  }
`;

export const FORCE_MAGENTO_SYNC = gql`
  mutation forceMagentoSync {
    forceMagentoSync {
      status
      startedAt
      endedAt
    }
  }
`;

export const START_CONVERSATION = gql`
  mutation startConversation($userInput: String!, $currentDate: String!) {
    startConversation(userInput: $userInput, currentDate: $currentDate) {
      id
      createdAt
      name
      messages {
        ${conversationMessageFields}
      }
    }
  }
`;

export const CONTINUE_CONVERSATION = gql`
  mutation continueConversation($conversationId: ID!, $userInput: String!, $currentDate: String!) {
    continueConversation(id: $conversationId, userInput: $userInput, currentDate: $currentDate) {
      ${conversationMessageFields}
    }
  }
`;

export const SET_CONVERSATION_MESSAGE_REACTION = gql`
  mutation setConversationMessageReaction($messageId: ID!, $helpful: Boolean!) {
    setConversationMessageReaction(messageId: $messageId, helpful: $helpful)
  }
`;

export const UPDATE_CONVERSATION_CONTEXT = gql`
  mutation updateConversationContext($args: UpdateConversationContextArgs!) {
    updateConversationContext(args: $args)
  }
`;

export const CREATE_CUSTOM_REPORT = gql`
  mutation createCustomReport($input: CustomReportInput!) {
    createCustomReport(input: $input) {
      id
      name
      config
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_CUSTOM_REPORT = gql`
  mutation updateCustomReport($id: String!, $input: CustomReportInput!) {
    updateCustomReport(id: $id, input: $input) {
      id
      name
      config
      createdAt
      updatedAt
    }
  }
`;

export const CREATE_KPI_SECTION = gql`
  mutation createKpiSection($input: KpiSectionInput!) {
    createKpiSection(input: $input) {
      id
      name
      config
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_KPI_SECTION = gql`
  mutation updateKpiSection($id: String!, $input: KpiSectionInput!) {
    updateKpiSection(id: $id, input: $input) {
      id
      name
      config
      createdAt
      updatedAt
    }
  }
`;

export const DELETE_KPI_SECTION = gql`
  mutation deleteKpiSection($id: String!) {
    deleteKpiSection(id: $id)
  }
`;

export const REORDER_KPI_SECTIONS = gql`
  mutation reorderKpiSections($orderedIds: [String!]!) {
    reorderKpiSections(orderedIds: $orderedIds) {
      id
      name
      config
      createdAt
      updatedAt
    }
  }
`;

// Mutation per generare report AI KPI
export const GENERATE_KPI_AI_REPORT = gql`
  mutation GenerateKpiAIReport($input: KpiAIReportInput!) {
    generateKpiAIReport(input: $input)
  }
`;

// Mutation JSON preflight: ritorna html e dati di confronto
export const GENERATE_KPI_AI_REPORT_DATA = gql`
  mutation GenerateKpiAIReportData($input: KpiAIReportInput!) {
    generateKpiAIReportData(input: $input)
  }
`;

export const UPDATE_MULTIPLE_METRIC_PERMISSIONS = gql`
  mutation UpdateMultipleMetricPermissions(
    $permissionsData: UpdateMultipleMetricPermissionsInput!
  ) {
    updateMultipleMetricPermissions(permissionsData: $permissionsData) {
      id
      metricKey
      connectorType
      isEnabled
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_USER = gql`
  mutation UpdateUser($input: UpdateUserInput!) {
    updateUser(input: $input) {
      id
      firstName
      lastName
      email
      role
      company {
        id
        name
      }
    }
  }
`;

export const CREATE_USER = gql`
  mutation CreateUser($input: CreateUserInput!) {
    createUser(input: $input) {
      id
      firstName
      lastName
      email
      role
      company {
        id
        name
      }
    }
  }
`;

export const DELETE_USER = gql`
  mutation DeleteUser($input: DeleteUserInput!) {
    deleteUser(input: $input)
  }
`;

const getMutationField = (
  connectorId: string,
  index: number,
  type: ConnectorType,
) => {
  switch (type) {
    case ConnectorType.GOOGlE_ANALYTICS4:
      return `
        selectAccounts${index}: selectAnalyticsAccounts(
          accountSelections: $accountSelections${index},
          connectorId: "${connectorId}"
        )
      `;

    case ConnectorType.GOOGLE_ADS:
      return `
        selectAccounts${index}: selectGoogleAdsAccounts(
          accountSelections: $accountSelections${index},
          connectorId: "${connectorId}"
        )
      `;

    case ConnectorType.FACEBOOK:
      return `
        selectAccounts${index}: selectFacebookAccounts(
          accountSelections: $accountSelections${index},
          connectorId: "${connectorId}"
        )
      `;

    case ConnectorType.GOOGLE_SEARCH_CONSOLE:
      return `
        selectAccounts${index}: selectGoogleSearchConsoleSites(
          accountSelections: $accountSelections${index},
          connectorId: "${connectorId}"
        )
      `;

    default:
      return '';
  }
};

export const createSelectAccountsMutation = (
  connectors: FormattedConnector[],
  type: ConnectorType,
) => {
  const mutations = connectors
    .map((connector, index) => getMutationField(connector.id, index, type))
    .join('\n');

  const mutationDocument = gql`
  mutation selectAccounts(${connectors
    .map((_, index) => {
      if (
        type === ConnectorType.FACEBOOK ||
        type === ConnectorType.GOOGLE_SEARCH_CONSOLE
      ) {
        return `$accountSelections${index}: [String!]!`;
      } else {
        return `$accountSelections${index}: [AccountSelectionInput!]!`;
      }
    })
    .join(', ')}) {
    ${mutations}
  }
`;

  const variables: Record<
    string,
    Array<AccountSelectionInput | string>
  > = connectors.reduce(
    (acc, connector, index) => {
      if (
        type === ConnectorType.FACEBOOK ||
        type === ConnectorType.GOOGLE_SEARCH_CONSOLE
      ) {
        acc[`accountSelections${index}`] = connector.accounts
          .filter((account) => account.selected)
          .map((account) => account.id);
      } else {
        acc[`accountSelections${index}`] = connector.accounts
          .filter((account) => account.selected)
          .map((account) => ({
            accountId: account.id,
            subaccountIds: account.properties
              .filter((prop) => prop.selected)
              .map((prop) => prop.id),
          }));
      }
      return acc;
    },
    {} as Record<string, Array<AccountSelectionInput | string>>,
  );

  return { mutationDocument, variables };
};

export const PLACEHOLDER_MUTATION = gql`
  mutation PlaceholderMutation {
    __typename
  }
`;

export const TEST_CONNECTION = gql`
  mutation TestConnection($connectionData: ConnectionInput!) {
    testConnection(connectionData: $connectionData) {
      success
      errorMessage
    }
  }
`;

export const CREATE_CONNECTION = gql`
  mutation CreateConnection($connectionData: ConnectionInput!) {
    createConnection(connectionData: $connectionData)
  }
`;

export const CREATE_DATABASE_CONNECTOR = gql`
  mutation CreateDatabaseConnector(
    $displayName: String!
    $connectionData: String!
    $selectedTables: String!
    $selectedSplits: String!
    $selectedMetrics: String!
  ) {
    createDatabaseConnector(
      displayName: $displayName
      connectionData: $connectionData
      selectedTables: $selectedTables
      selectedSplits: $selectedSplits
      selectedMetrics: $selectedMetrics
    )
  }
`;
